import dayjs from 'dayjs'
import { defineStore } from 'pinia'
import { ACTION_TYPE } from '../enums/hotspot-enum'
import { FORM_POSITION } from '../enums/form-enum'

export const usePlayerStore = defineStore('player', {
    state: () => ({
        activeHotspot: null,
        cloudFrontURL: '',
        countriesData: [],
        currentTimePlayer: 0,
        currentResolutionSelected: 0,
        dataVariable: [],
        email: '',
        formClickedFromSidebarData: null,
        formClickedFromSidebarConfigurationData: '',
        formData: [],
        formID: 0,
        formQuestions: [],
        fullscreen: false,
        hasFrame: false,
        registerHotspotData: null,
        hotspotAbsoluteRouteURL: '',
        hotspotsData: [],
        isAutoPlay: false,
        isDebug: 'false',
        isFetchingBtnUnmuteAutoplay: true,
        isVideoMuted: false,
        logEvents: [],
        modeAdmin: false,
        objPlayer: [],
        overlayLayoutCoordinates: {
            width: 0,
            height: 0,
            top: 0,
            left: 0,
        },
        pathLocalOrServe: '',
        playerIsReady: false,
        playerMetrics: [],
        questionsAnswered: {},
        shareVideoStatus: false,
        shareData: [],
        showTimeline: false,
        sidebar: [],
        subscriptionActive: false,
        timeHotspotInteraction: { interactionElapsedTime: 0, interactionTimeStamp: dayjs() },
        timePlayedStart: 0,
        timeRangePlayed: [],
        videoData: [],
        videoSettings: {
            orientation: 'horizontal',
            videoIsPlaying: false,
            videoWasExecuted: false,
        },
        videoThumbnails: [],
        videoID: 0,
        weblinkID: 0,
        weblinkIDEncore: ''
    }),

    getters: {
        getActiveHotspot: (state) => state.activeHotspot,
        getCloudFrontURL: (state) => state.cloudFrontURL,
        getCountriesData: (state) => state.countriesData,
        getCurrentResolutionSelected: (state) => state.currentResolutionSelected,
        getCurrentTimePlayer: (state) => state.currentTimePlayer,
        getDataVariable: (state) => state.dataVariable,
        getDataVariableByVariableName: (state) => (variableName) => {
            if (state.dataVariable == null) {
                return [];
            }
            return state.dataVariable.filter((dataItem) => (
                dataItem.fieldExcel.description === variableName
            ));
        },
        getDataVariableById: (state) => (variableId) => {
            if (state.dataVariable == null) {
                return [];
            }
            return state.dataVariable.filter((dataItem) => (
                dataItem.fieldExcel.fieldExcelId === variableId
            ));
        },
        getEmail: (state) => state.email,
        getFormClickedFromSidebarData(state) {
            return state.formClickedFromSidebarData;
        },
        getFormClickedFromSidebarConfigurationData(state) {
            return state.formClickedFromSidebarConfigurationData;
        },
        getFormData: (state) => state.formData,
        getFormID: (state) => state.formID,
        getFormQuestions: (state) => (id, type) => {
            if (state.formQuestions == null) {
                return [];
            }

            return state.formQuestions.filter((dataItem) => (
                dataItem.id == id && dataItem.type == type
            ));
        },
        getFullScreen: (state) => state.fullscreen,
        getFormsBySize() {
            return (size) => {
                if (size === 'large') {
                    return this.getHotspotsByActionType(ACTION_TYPE.FORM).filter((hotspot) => (
                        hotspot.hotspotFormPositions.hotspotFormPositionId === FORM_POSITION.LEFT
                        || hotspot.hotspotFormPositions.hotspotFormPositionId === FORM_POSITION.RIGHT
                        || hotspot.hotspotFormPositions.hotspotFormPositionId === FORM_POSITION.CENTER
                    ));
                } if (size === 'small') {
                    return this.getHotspotsByActionType(ACTION_TYPE.FORM).filter((hotspot) => (
                        hotspot.hotspotFormPositions.hotspotFormPositionId === FORM_POSITION.TOP_LEFT
                        || hotspot.hotspotFormPositions.hotspotFormPositionId === FORM_POSITION.TOP_RIGHT
                        || hotspot.hotspotFormPositions.hotspotFormPositionId === FORM_POSITION.BOTTOM_LEFT
                        || hotspot.hotspotFormPositions.hotspotFormPositionId === FORM_POSITION.BOTTOM_RIGHT
                        || hotspot.hotspotFormPositions.hotspotFormPositionId === FORM_POSITION.CENTER_SMALL
                    ));
                }
                return [];
            };
        },
        getHasFrame: (state) => state.hasFrame,
        getHotspotAbsoluteRouteURL: (state) => state.hotspotAbsoluteRouteURL,
        getHotspotsByActionType: (state) => (typeID) => state.hotspotsData.filter((hotspot) => hotspot.hotspotAction.actionType.actionTypeId === typeID),
        getHotspotsData: (state) => state.hotspotsData,
        getIsAutoPlay: (state) => state.isAutoPlay,
        getIsDebug: (state) => state.isDebug,
        getIsFetchingBtnUnmuteAutoplay: (state) => state.isFetchingBtnUnmuteAutoplay,
        getIsVideoMuted: (state) => state.isVideoMuted,
        getLogEvents: (state) => state.logEvents,
        getModeAdmin: (state) => state.modeAdmin,
        getOverlayLayoutCoordinates: (state) => state.overlayLayoutCoordinates,
        getPathLocalOrServer: (state) => state.pathLocalOrServe,
        getPlayer: (state) => state.objPlayer,
        getPlayerIsReady: (state) => state.playerIsReady,
        getPlayerMetrics: (state) => state.playerMetrics,
        getQuestionsAnswered: (state) => state.questionsAnswered,
        getQuestionsAnsweredByQuestion: (state) => (questionId) => {
            let keys = Object.keys( state.questionsAnswered );
            let result = null;
            for (let i = 0; i < keys.length; i++){
                if (keys[i] === questionId){
                    result = state.questionsAnswered[keys[i]];
                }
            }
            return result;
        },
        getQuestionsByHotspot: (state) => (hotspotId, formSize) => {
            if (state.formData.length == 0) {
                return [];
            }

            const isRegister = !!state.registerHotspotData && state.registerHotspotData.hotspotId === hotspotId
            if (formSize === 'large') {
                return state.formData.questions.filter((question) => {
                    if (question.hotspotId.toString().includes(String(hotspotId))) {
                        return true;
                    }
                    if (isRegister) {
                        // cuando es de registro, se ignora el mustRequired????????
                        return false
                    }

                    //Here we remove the must_required question that has been answered
                    return (question.mustRequired == 1 && !Object.keys(state.questionsAnswered).includes(String(question.questionId)))
                });
            }
            return state.formData.questions.filter((question) => (
                question.hotspotId == hotspotId
            ));
        },
        getShareData: (state) => state.shareData,
        getShareVideoStatus(state) {
            return state.shareVideoStatus;
        },
        getShowTimeline: (state) => state.showTimeline,
        getSidebar: (state) => state.sidebar,
        getSubscriptionActive: (state) => state.subscriptionActive,
        getTimeHotspotInteraction: (state) => state.timeHotspotInteraction,
        getTimePlayedStart: (state) => state.timePlayedStart,
        getTimeRangePlayed: (state) => state.timeRangePlayed,
        getVideoData: (state) => state.videoData,
        getVideoIsPlaying: (state) => state.videoSettings.videoIsPlaying,
        getVideoOrientation: (state) => state.videoSettings.orientation,
        getVideoThumbnails: (state) => state.videoThumbnails,
        getVideoWasExecuted: (state) => state.videoSettings.videoWasExecuted,
        getVideoID: (state) => state.videoID,
        getWeblinkID: (state) => state.weblinkID,
        getWeblinkIDEncore: (state) => state.weblinkIDEncore,
        isActiveHotspotRegistration: (state) => {
            return !!state.activeHotspot
              && !!state.registerHotspotData
              && state.activeHotspot.hotspotId === state.registerHotspotData.hotspotId;
        },
        isHotspotRegisterType: () => {
            return (hotspot) => hotspot.hotspotType?.hotspotTypeId === 6
        },
    },

    actions: {
        setActiveHotspot(value) {
            this.activeHotspot = value;
        },
        setAnswerInFormQuestions(value) {
            this.formQuestions.forEach((form) => {
                form.data.forEach((question) => {
                    if (question.questionId === value.questionId) {
                        question.answer = value.answer;
                    }
                });
            });
        },
        setAnswerOtherInFormQuestions(value) {
            this.formQuestions.forEach((form) => {
                form.data.forEach((question) => {
                    if (question.questionId === value.questionId) {
                        question.answerOther = value.answerOther;
                    }
                });
            });
        },
        setCloudFrontURL(value) {
            this.cloudFrontURL = value;
        },
        setCountriesData(value) {
            this.countriesData = value;
        },
        setCurrentResolutionSelected(value) {
            this.currentResolutionSelected = value;
        },
        setCurrentTimePlayer(value) {
            this.currentTimePlayer = value;
        },
        setDataVariable(value) {
            this.dataVariable = value;
        },
        setEmail(value) {
            this.email = value;
        },
        setFormClickedFromSidebarData(value) {
            const { data } = value
            this.formClickedFromSidebarData = data;
        },
        setFormData(value) {
            this.formData = value;
        },
        setFormID(value) {
            this.formID = value;
        },
        setFormQuestions(value) {
            this.formQuestions.push(value);
        },
        setFullScreen(value) {
            this.fullscreen = value;
        },
        setRegisterHotspotData (data) {
            this.registerHotspotData = data;
        },
        setHotspotAbsoluteRouteURL(value) {
            this.hotspotAbsoluteRouteURL = value;
        },
        setHasFrame(value) {
            this.hasFrame = value;
        },
        setHotspotsData(value) {
            this.hotspotsData = value;
        },
        setIsAutoPlay(value) {
            this.isAutoPlay = value;
        },
        setIsDebug(value) {
            this.isDebug = value;
        },
        setIsFetchingBtnUnmuteAutoplay(value) {
            this.isFetchingBtnUnmuteAutoplay = value;
        },
        setIsVideoMuted(value) {
            this.isVideoMuted = value;
        },
        setLogEvents(value) {
            this.logEvents.push(value);
        },
        setModeAdmin(value) {
            this.modeAdmin = value;
        },
        setOverlayLayoutCoordinates(value) {
            this.overlayLayoutCoordinates = value;
        },
        setPathLocalOrServe(value) {
            this.pathLocalOrServe = value;
        },
        setPlayer(value) {
            this.objPlayer = value;
        },
        setPlayerIsReady(value) {
            this.playerIsReady = value;
        },
        setPlayerMetrics(value) {
            this.playerMetrics = value;
        },
        setQuestionsAnswered(value) {
            this.questionsAnswered = { ...this.questionsAnswered, ...value };
        },
        setReorderTimeRangePlayed(value) {
            this.timeRangePlayed = value;
        },
        setShareData(value) {
            this.shareData = value;
        },
        setShareVideoStatus() {
            this.shareVideoStatus = !this.shareVideoStatus;
        },
        setShowTimeline(value) {
            this.showTimeline = value;
        },
        setSidebar(value) {
            this.sidebar = value;
        },
        setSubscriptionActive(value) {
            this.subscriptionActive = value;
        },
        setTimeHotspotInteraction() {
            this.timeHotspotInteraction = {
                interactionElapsedTime: dayjs().diff(this.timeHotspotInteraction.interactionTimeStamp, 'second'),
                interactionTimeStamp: dayjs(),
            };
        },
        setTimePlayedStart(value) {
            this.timePlayedStart = value;
        },
        setTimeRangePlayed(value) {
            this.timeRangePlayed.push(value);
        },
        setVideoData(value) {
            this.videoData = value;
        },
        setVideoIsPlaying(value) {
            this.videoSettings.videoIsPlaying = value;
        },
        setVideoOrientation(value) {
            this.videoSettings.orientation = value;
        },
        setVideoThumbnails(value) {
            this.videoThumbnails = value;
        },
        setVideoWasExecuted(value) {
            this.videoSettings.videoWasExecuted = value;
        },
        setVideoID(value) {
            this.videoID = value;
        },
        setWeblinkID(value) {
            this.weblinkID = value;
        },
        setWeblinkIDEncore(value) {
            this.weblinkIDEncore = value;
        },
        addActiveHotspot(value) {
            this.setActiveHotspot(value);
            this.setTimeHotspotInteraction();
        },
        initializeFromSettings (settings) {
            this.setVideoData(settings.videoData)
            this.setFormData(settings.formData.form)
            this.setFormID(settings.formData !== null && settings.formData.form ? parseInt(settings.formData.form.formId) : 0)

            let hotspots = settings.hotspotData
            const registrationHotspot = hotspots.find(this.isHotspotRegisterType)
            if (registrationHotspot) {
                this.setRegisterHotspotData(registrationHotspot)

                hotspots = hotspots.filter(hotspot => {
                    return hotspot.hotspotId !== registrationHotspot.hotspotId
                })
            }

            this.setHotspotsData(hotspots)
            this.setPathLocalOrServe(settings.routeURL)
            this.setCloudFrontURL(settings.cloudfrontURL)
            this.setShowTimeline(true)
            this.setSubscriptionActive(settings.subscriptionActive)
            this.setSidebar(settings.sidebarData)
            this.setShareData(settings.shareData)
            this.setCountriesData(settings.countriesData)
            this.setDataVariable(settings.dataVariable)
            this.setVideoThumbnails(settings.videoThumbnail)

            if (registrationHotspot) {
                this.addActiveHotspot(registrationHotspot)
                this.setIsAutoPlay(false)
            } else {
                // todo: no debe hacer autoplay con form de elearning?
                this.setIsAutoPlay(settings.isAutoPlay)
            }

            this.initializeFormQuestions();
        },
        initializeFormQuestions() {
            this.formQuestions = [];

            const registerQuestions = (hotspot, size) => {
                const questionData = {
                    'id': hotspot.hotspotId,
                    'type': 'hotspot',
                    'data': this.getQuestionsByHotspot(hotspot.hotspotId, size)
                }
                this.setFormQuestions(questionData);
            }

            this.getFormsBySize('large').forEach(h => registerQuestions(h, 'large'));
            this.getFormsBySize('small').forEach(h => registerQuestions(h, 'small'));

            if (this.getFormData && this.getFormData.questions) {
                const questionData = {
                    'id': this.getFormID,
                    'type': 'form',
                    'data': this.getFormData.questions
                }
                this.setFormQuestions(questionData);
            }

            if (this.registerHotspotData) {
                registerQuestions(this.registerHotspotData, 'large')
            }
        }
    }
});
